// Money formatting for the AI spend panel.
//
// EXTRACTED SO IT CAN BE TESTED. Inline in the component it was untestable, and
// it took the whole application down: the provider feed returns cost as an
// OBJECT ({amount, currency, unit}), money() called .toFixed on it, the
// exception reached the error boundary, and every screen was replaced with
// "Something went wrong". Type checking passed the entire time, because the
// TypeScript type I wrote described a shape the service does not return.
//
// That is CLAUDE.md §4 exactly: a screen is not verified by anything short of
// looking at it. Only the owner opening the page found this.

/** Never throws. A shape surprise must cost one cell, not the application. */
export const money = (v: unknown, ccy = "USD"): string => {
  const n = typeof v === "number" ? v : Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return "not priced";
  const sym = ccy === "USD" ? "$" : `${ccy} `;
  if (n === 0) return `${sym}0`;
  if (Math.abs(n) < 0.01) return `${sym}${n.toFixed(6)}`;
  return `${sym}${n.toFixed(4)}`;
};

/** Pull the amount out of whichever shape a provider row carries. */
export const amountOf = (c: unknown): number | null => {
  if (typeof c === "number") return c;
  if (c && typeof c === "object" && "amount" in (c as Record<string, unknown>)) {
    const a = (c as Record<string, unknown>).amount;
    return typeof a === "number" ? a : null;
  }
  return null;
};

export const currencyOf = (c: unknown, fallback = "USD"): string =>
  c && typeof c === "object" && typeof (c as Record<string, unknown>).currency === "string"
    ? ((c as Record<string, unknown>).currency as string)
    : fallback;

/**
 * Pivot the provider's per-model rows into one row per model.
 *
 * The provider returns 89 flat entries whose `model` field jams the model and
 * the direction into one string — "gpt-5.6-luna, input". Rendered literally
 * that is 89 lines of near-identical text, which is what the owner saw and
 * called unreadable. Pivoting gives roughly eight rows with columns.
 *
 * The split is on the LAST comma, because a model name may contain one and the
 * direction never does.
 */
export type ModelCost = { model: string; kinds: Record<string, number>; total: number };

export const groupCostByModel = (
  rows: { model?: string; amount?: number | null }[] | null | undefined,
): { models: ModelCost[]; kinds: string[] } => {
  const byModel = new Map<string, ModelCost>();
  const kindSet = new Set<string>();

  for (const r of rows || []) {
    const raw = typeof r?.model === "string" ? r.model : "";
    const amt = typeof r?.amount === "number" && Number.isFinite(r.amount) ? r.amount : null;
    if (!raw || amt === null) continue;

    const i = raw.lastIndexOf(",");
    const model = (i === -1 ? raw : raw.slice(0, i)).trim() || raw;
    const kind = (i === -1 ? "total" : raw.slice(i + 1)).trim() || "total";

    kindSet.add(kind);
    const entry = byModel.get(model) || { model, kinds: {}, total: 0 };
    entry.kinds[kind] = (entry.kinds[kind] || 0) + amt;
    entry.total += amt;
    byModel.set(model, entry);
  }

  // Most expensive first: the reason to open this table is to find where the
  // money went, not to read an alphabet.
  const models = [...byModel.values()].sort((a, b) => b.total - a.total);

  // Stable, meaningful column order; anything unexpected keeps its own column
  // rather than being dropped into "other", so a new billing category is
  // visible rather than silently folded away.
  const preferred = ["input", "cached input", "output"];
  const kinds = [
    ...preferred.filter((k) => kindSet.has(k)),
    ...[...kindSet].filter((k) => !preferred.includes(k)).sort(),
  ];
  return { models, kinds };
};
